import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Asset,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
  Icon,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  cn,
} from '@kognitos/lattice';
import { useListAssets } from '@/generated/hooks/bishop/collections/v1/api';
import { serializeParams } from '@/shared/utils';
import { FileDropzone } from '@/shared/components';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import {
  ACCEPTED_ASSET_FILE_TYPES,
  getAssetSortOrderBy,
  getAssetTypeFromMimeType,
  toAssetView,
} from '../utils';
import type { IAssetView } from '../types';
import { AssetManagerTree } from './AssetManagerTree';

// Number of recent assets surfaced in the persistent Recents strip. There is no
// dedicated recents endpoint yet, so this is backed by the most-recently-modified
// assets from ListAssets.
const RECENTS_PAGE_SIZE = 4;
// Shared height for both tab panels so switching tabs doesn't resize the dialog.
const TAB_CONTENT_HEIGHT = 'h-[320px]';

type TTab = 'upload' | 'assetManager';

export interface ISelectOrUploadFileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called with the assets the user selected (Recents + Assets) when they
   * click Save. The consumer decides what to do with them — e.g. attach their
   * `reference_uri` / `file_name` to a message.
   */
  onConfirm?: (assets: IAssetView[]) => void;
  /**
   * Called with the local files the user picked on the Upload tab. Uploading is
   * offloaded to the consumer (the dialog closes immediately) so it can handle
   * upload however it wants — e.g. stage them as chat attachments to upload on
   * send, exactly like the native file picker / drag-drop.
   */
  onUploadFiles?: (files: File[]) => void;
}

export function SelectOrUploadFileDialog({
  isOpen,
  onClose,
  onConfirm,
  onUploadFiles,
}: ISelectOrUploadFileDialogProps) {
  const { t } = useTranslation('library');

  const [activeTab, setActiveTab] = useState<TTab>('upload');
  // Selected assets (Recents + Assets) keyed by asset resource name.
  const [selectedAssets, setSelectedAssets] = useState<Map<string, IAssetView>>(
    new Map(),
  );
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Workspace-wide most-recent assets. order_by carries a space, so the qs
  // serializer is required — axios's default encodes it as `+`, which bishop
  // rejects with a 400.
  const { data: recentsData, isLoading: isLoadingRecents } = useListAssets(
    orgId,
    workspaceId,
    {
      page_size: RECENTS_PAGE_SIZE,
      folder_name: '*',
      order_by: getAssetSortOrderBy('modified', 'desc'),
    },
    { axios: { paramsSerializer: serializeParams } },
  );
  const recents = (recentsData?.data?.assets ?? []).map(toAssetView);

  const selectionCount = selectedAssets.size;

  const reset = () => {
    setSelectedAssets(new Map());
    setActiveTab('upload');
  };

  const closeAndReset = () => {
    reset();
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) closeAndReset();
  };

  const toggleAsset = (asset: IAssetView) => {
    const key = asset.name;
    if (!key) return;
    setSelectedAssets(prev => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, asset);
      }
      return next;
    });
  };

  // Upload is offloaded to the consumer: hand over the picked files and close.
  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    onUploadFiles?.(Array.from(fileList));
    closeAndReset();
  };

  const handleSave = () => {
    onConfirm?.(Array.from(selectedAssets.values()));
    closeAndReset();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('library.dialog.selectOrUpload.title')}</DialogTitle>
          <DialogDescription>
            {t('library.dialog.selectOrUpload.description')}
          </DialogDescription>
        </DialogHeader>

        <Flex direction="column" gap={5} width="full" className="min-w-0">
          <Tabs
            value={activeTab}
            onValueChange={value => setActiveTab(value as TTab)}
            className="w-full"
          >
            <Flex
              direction="column"
              gap={4}
              width="full"
              padding={2}
              className="rounded-md border border-border"
            >
              <TabsList className="w-full">
                <TabsTrigger value="upload" className="flex-1">
                  {t('library.dialog.selectOrUpload.tabs.upload')}
                </TabsTrigger>
                <TabsTrigger value="assetManager" className="flex-1">
                  <Icon type="Folder" size="sm" />
                  {t('library.dialog.selectOrUpload.tabs.assetManager')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="w-full">
                <FileDropzone
                  inputId="select-or-upload-file-input"
                  label={t('library.dialog.selectOrUpload.dropzone')}
                  onFiles={handleFiles}
                  accept={ACCEPTED_ASSET_FILE_TYPES}
                  className={TAB_CONTENT_HEIGHT}
                  multiple
                />
              </TabsContent>

              <TabsContent value="assetManager" className="w-full">
                <AssetManagerTree
                  onAssetSelect={toggleAsset}
                  selectedAssetNames={new Set(selectedAssets.keys())}
                />
              </TabsContent>
            </Flex>
          </Tabs>

          <Flex direction="column" gap={2} width="full">
            <Text weight="medium">
              {t('library.dialog.selectOrUpload.recents')}
            </Text>
            {isLoadingRecents ? (
              <div className="grid grid-cols-2 gap-2">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            ) : recents.length === 0 ? (
              <Text level="small" color="muted">
                {t('library.dialog.selectOrUpload.recentEmpty')}
              </Text>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {recents.map(asset => {
                  const isSelected = selectedAssets.has(asset.name ?? '');
                  return (
                    <div
                      key={asset.name}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      onClick={() => toggleAsset(asset)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggleAsset(asset);
                        }
                      }}
                      className={cn(
                        'rounded-md transition-shadow',
                        isSelected && 'ring-2 ring-primary',
                      )}
                    >
                      <Asset
                        variant="default"
                        type={getAssetTypeFromMimeType(asset.mime_type)}
                        name={asset.display_name ?? ''}
                        // TODO: bishop doesn't return version / "updated by"
                        // display metadata yet, so no subtitle for now.
                        showActionIcon={false}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </Flex>
        </Flex>

        <DialogFooter>
          <Button variant="outline" onClick={closeAndReset}>
            {t('library.actions.cancel')}
          </Button>
          <Button disabled={selectionCount === 0} onClick={handleSave}>
            {selectionCount > 0
              ? t('library.dialog.selectOrUpload.saveCount', {
                  count: selectionCount,
                })
              : t('library.dialog.selectOrUpload.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SelectOrUploadFileDialog;
